import moment from 'moment';
import * as queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { showDialog } from '../../../core/actions';
import InlineEditTable from '../../../core/components/InlineEditTable/InlineEditTable';
import { addPermissionRefs } from '../../../core/components/UserGroupPermissions';
import { selectCoreSiteId } from '../../settings/reducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import * as actions from '../actions';
import { getApiStatus } from '../../../core/utils';
import InventoryDetail from './InventoryDetail';
import { buildInventoryColumns } from './inventoryTableConstants';
import {
  selectInventory,
  selectInventoryTypes,
  selectPage,
  selectPageSize,
  selectTotal,
  selectInventoryInEdit,
  selectFromDate,
  selectUntilDate,
  selectTypeFilter,
} from '../reducer';
import { Drawer, DrawerContainer } from '../../../core/components/Drawer';
import UserGroupPermissions from '../../../core/components/UserGroupPermissions';
import Button from '../../../core/components/Button';
import { getSiteLabel } from '../../site-selection/helpers';
import { selectSitesBasic } from '../../site-selection/reducer';
import { FilterPlacement } from '../../../core/components/PageFilters';
import { IoSearchSharp } from 'react-icons/io5';
import { FaBarcode, FaBoxes, FaTags } from 'react-icons/fa';

const Inventory = (props) => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const query = queryString.parse(location.search);
  const inventory = useSelector(selectInventory);
  const inventoryTypes = useSelector(selectInventoryTypes);
  const filter = useSelector(selectTypeFilter);
  const total = useSelector(selectTotal);
  const page = useSelector(selectPage);
  const pageSize = useSelector(selectPageSize);
  const sites = useSelector(selectSitesBasic);
  const siteId = useSelector(selectCoreSiteId);
  const multiSites = useSelector(selectMultiSiteSelection);
  const [inventoryExpanded, setExpanded] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(0);
  const [editingStockId, setEditingStockId] = useState(null);
  const [inventoryStockLevels, setInventoryStockLevels] = useState(new Map());
  const [tableFilterValues, setTableFilterValues] = useState({
    searchText: '',
    inventoryTypeId: [],
    partNumberRaw: '',
    stockLevelStatus: [],
  });
  const inventoryInEdit = useSelector(selectInventoryInEdit);
  const inventoryAdded = useSelector((state) => getApiStatus(state, 'ADD_INVENTORY_ITEM'));
  const inventoryEdited = useSelector((state) => getApiStatus(state, 'SAVE_INVENTORY_ITEM'));
  const inventoryDeleted = useSelector((state) => getApiStatus(state, 'DELETE_INVENTORY_ITEM'));
  const inventoryFetching = useSelector((state) => getApiStatus(state, 'FETCH_INVENTORY'));

  //Search variables
  const fromDate = useSelector(selectFromDate);
  const untilDate = useSelector(selectUntilDate);

  const getInventoryQuery = (pageNumber = 0) => ({
    page: pageNumber,
    inventoryType: filter,
    fromDate,
    untilDate,
    searchText: tableFilterValues.searchText,
    inventoryTypeId: tableFilterValues.inventoryTypeId,
    partNumberRaw: tableFilterValues.partNumberRaw,
    stockLevelStatus: tableFilterValues.stockLevelStatus,
  });

  useEffect(() => {
    updateInventory();
  }, [
    filter,
    tableFilterValues,
    siteId,
    multiSites,
    fromDate,
    untilDate,
    inventoryAdded,
    inventoryEdited,
    inventoryDeleted,
  ]);

  useEffect(() => {
    dispatch(actions.fetchInventoryTypes());
  }, [siteId]);

  useEffect(() => {
    setExpanded(false);
    setHighlightedRow(0);
  }, [filter]);

  const getInventoryPage = (selectedPage) => {
    dispatch(actions.fetchInventory(getInventoryQuery(selectedPage)));
  };

  const getPaginationInfo = () => {
    return {
      total: total,
      page: page,
      pageSize: pageSize,
      goToPage: getInventoryPage,
    };
  };

  const clearInventoryInEdit = () => {
    setHighlightedRow(0);
    setExpanded(false);
    dispatch(actions.deleteInventoryInEdit());
  };

  const updateInventory = () => {
    dispatch(actions.fetchInventory(getInventoryQuery(0)));
  };

  const addInventory = () => {
    let dialogProps = {
      onSubmit: (item) => dispatch(actions.addInventoryItem(item)),
      isNew: true,
      title: intl.formatMessage({ id: 'addInventoryItem' }),
      intl: intl,
    };
    dispatch(showDialog('UPSERT_INVENTORY', dialogProps));
  };

  const getRowClickInfo = () => ({
    callback: (data) => {
      if (editingStockId) {
        stopStockEditing();
        return;
      }
      setExpanded(true);
      setHighlightedRow(data._id);
      dispatch(actions.getInventoryItem(data._id));
    },
  });

  const setInventoryStock = (inventoryId, stockLevel) => {
    const newMap = new Map(inventoryStockLevels);
    newMap.set(inventoryId, stockLevel);
    setInventoryStockLevels(newMap);
  };

  const getInventoryStock = (inventoryId) => {
    return inventoryStockLevels.get(inventoryId);
  };

  const clearInventoryStock = (inventoryId) => {
    const newMap = new Map(inventoryStockLevels);
    if (newMap.delete(inventoryId)) {
      setInventoryStockLevels(newMap);
    }
  };

  const handleStockCommit = (inventoryId, stockLevel) => {
    const parsed = Number(stockLevel);
    if (!Number.isFinite(parsed)) return;
    dispatch(actions.updateInventoryStock({ stockLevels: [{ id: inventoryId, number: parsed }] }));
  };

  const stopStockEditing = () => {
    setEditingStockId(null);
  };

  const startStockEditing = (inventoryId) => {
    setEditingStockId(inventoryId);
  };

  const trailDescription = (description) => {
    if (description && description.length > 100) {
      return description.substring(0, 100) + '...';
    }
    return description;
  };

  const buildRows = (initialInventory) => {
    const typeLabelMap = new Map(
      (inventoryTypes || []).map((type) => [String(type.id), type.name])
    );
    return initialInventory.map((i) => {
      const partNum =
        i.number ?? i.partNumber ?? i.part_number ?? i.inventoryNumber ?? i.inventory_number ?? '';
      const description = i.description || '';
      const searchText = `${i.name || ''} ${partNum} ${description}`.trim();
      const inventoryTypeId =
        i.inventoryTypeId ?? i.inventoryType?.id ?? i.type ?? i.inventoryType ?? 0;
      const typeLabel =
        i.inventoryTypeName ??
        i.inventoryType?.name ??
        typeLabelMap.get(String(inventoryTypeId)) ??
        '';
      const preferredStockLevel = i.preferredStockLevel;
      const onHand = getInventoryStock(i.id) ?? i.onHand;
      let stockLevelStatus = '';
      if (preferredStockLevel && preferredStockLevel > 0) {
        if (onHand === 0) {
          stockLevelStatus = 'out';
        } else {
          const ratio = onHand / preferredStockLevel;
          if (ratio > 1) {
            stockLevelStatus = 'over';
          } else if (ratio === 1) {
            stockLevelStatus = 'in';
          } else if (ratio > 0.4) {
            stockLevelStatus = 'in';
          } else {
            stockLevelStatus = 'low';
          }
        }
      } else if (onHand === 0) {
        stockLevelStatus = 'out';
      } else if (onHand != null && onHand !== '') {
        stockLevelStatus = 'in';
      }
      return {
        _id: i.id,
        partNumber: trailDescription(partNum),
        partNumberRaw: partNum,
        partNumberDisplay: i.priority ? (
          <span className="inventory-name">
            <span className="inventory-priority">
              <i className="uil uil-clipboard-notes"></i>
            </span>{' '}
            {trailDescription(partNum)}
          </span>
        ) : (
          trailDescription(partNum)
        ),
        name: i.name || '',
        nameDisplay: (
          <div>
            <div>{i.name || ''}</div>
            <div className="task-subtitle">
              {typeof description === 'string' && description.length > 50
                ? description.slice(0, 47) + '...'
                : description}
            </div>
          </div>
        ),
        inventoryTypeId,
        inventoryTypeName: typeLabel,
        searchText,
        stockLevelStatus,
        site: getSiteLabel(sites, i.siteId),
        _preferredStockLevel: preferredStockLevel,
        _onHand: onHand,
        lastUpdated: i.modifiedAt ? moment(i.modifiedAt).format('MM/DD/YYYY') : '',
      };
    });
  };

  const drawerControlsConfig = () => {
    return {
      closeFunction: () => {
        clearInventoryInEdit();
      },
    };
  };

  const rows = useMemo(
    () => buildRows(inventory),
    [inventory, inventoryTypes, inventoryStockLevels]
  );
  const columns = buildInventoryColumns({
    intl,
    editingStockId,
    setInventoryStock,
    clearStock: clearInventoryStock,
    onStockCommit: handleStockCommit,
    onStopEditing: stopStockEditing,
    onStartEditing: startStockEditing,
  });
  const filterConfigs = useMemo(() => {
    const typeOptions = (inventoryTypes || []).map((type) => ({
      value: String(type.id),
      text: type.name,
      selected: (tableFilterValues.inventoryTypeId || []).map(String).includes(String(type.id)),
    }));
    const stockStatusOptions = [
      {
        value: 'out',
        text: intl.formatMessage({ id: 'outOfStock', defaultMessage: 'Out of Stock' }),
      },
      {
        value: 'low',
        text: intl.formatMessage({ id: 'lowStock', defaultMessage: 'Low Stock' }),
      },
      {
        value: 'in',
        text: intl.formatMessage({ id: 'inStock', defaultMessage: 'In Stock' }),
      },
      {
        value: 'over',
        text: intl.formatMessage({ id: 'overstocked', defaultMessage: 'Overstocked' }),
      },
    ].map((option) => ({
      ...option,
      selected: (tableFilterValues.stockLevelStatus || [])
        .map(String)
        .includes(String(option.value)),
    }));
    return [
      {
        id: 'searchText',
        type: 'search',
        name: intl.formatMessage({ id: 'items', defaultMessage: 'Items' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        debounceMs: 0,
        searchTerm: tableFilterValues.searchText,
        onChange: (value) => setTableFilterValues((prev) => ({ ...prev, searchText: value || '' })),
      },
      {
        id: 'inventoryTypeId',
        type: 'select',
        name: intl.formatMessage({ id: 'type', defaultMessage: 'Type' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaTags />,
        options: typeOptions,
        onChange: (values) =>
          setTableFilterValues((prev) => ({ ...prev, inventoryTypeId: values })),
      },
      {
        id: 'partNumberRaw',
        type: 'search',
        name: intl.formatMessage({ id: 'partNumber', defaultMessage: 'Part Number' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaBarcode />,
        debounceMs: 0,
        searchTerm: tableFilterValues.partNumberRaw,
        onChange: (value) =>
          setTableFilterValues((prev) => ({ ...prev, partNumberRaw: value || '' })),
      },
      {
        id: 'stockLevelStatus',
        type: 'select',
        name: intl.formatMessage({ id: 'stockLevelStatus', defaultMessage: 'Stock Level Status' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaBoxes />,
        options: stockStatusOptions,
        onChange: (values) =>
          setTableFilterValues((prev) => ({ ...prev, stockLevelStatus: values })),
      },
    ];
  }, [intl, inventoryTypes, tableFilterValues]);
  const headerActions = (
    <UserGroupPermissions itemRef={addPermissionRefs.inventory}>
      <Button className="button primary" onClick={() => addInventory()}>
        {intl.formatMessage({ id: 'addInventoryItem' })}
      </Button>
    </UserGroupPermissions>
  );

  return (
    <section className="inventory">
      <DrawerContainer>
        <div>
          <InlineEditTable
            paginationInfo={getPaginationInfo()}
            highlightedRow={highlightedRow}
            rows={rows}
            columns={columns}
            filterConfigs={filterConfigs}
            headerActions={headerActions}
            isLoading={inventoryFetching}
            rowClickInfo={getRowClickInfo()}
            paginateLocally={false}
          />
        </div>
        <Drawer expanded={inventoryExpanded}>
          <div className="inventory-details-wrapper">
            {inventoryInEdit && (
              <InventoryDetail isPage={false} drawerControls={{ ...drawerControlsConfig() }} />
            )}
          </div>
        </Drawer>
      </DrawerContainer>
    </section>
  );
};

export default Inventory;
